import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@shared/hooks';
import { useToast } from '@retail/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { generateXLSX, XLSXRow } from '@retail/products/utils';
import { fetchPaginatedDataInParallel } from '@shared/fetch-utils';
import { useMutateFetchSupplierItemsInsightReport } from '@retail/products/data-access';
import {
  EnvironmentFilter,
  IncludesOrMissing,
  ProductGroup,
  SupplierItemInsight,
} from '@retail/products/types';
import { getISO_8601Date } from '@retail/utils';
import { supplierItemInsightKeyPaths } from './supplierItemInsightKeyPaths';
import { SupplierBase } from '@retail/suppliers/types';

const rowHeaders = [supplierItemInsightKeyPaths];

interface Props {
  selectedSupplier: SupplierBase | null;
  selectedProductGroup: ProductGroup | null;
  searchQuery: string | null;
  articleId?: number;
  environmentValuesInterface: EnvironmentFilter;
  climateDataA1ToA3Included: boolean;
  includedOrMissingFieldValue: IncludesOrMissing;
}

type PrimitiveCell = string | number | boolean | null | undefined;

function getValueByPath(obj: unknown, path: string): PrimitiveCell {
  if (!path) return undefined;

  const value = path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    if (typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);

  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value;

  return undefined;
}

const getInsightReportItemRows = (insightItems: SupplierItemInsight[]): XLSXRow[] =>
  insightItems.map((insightItem) =>
    supplierItemInsightKeyPaths.map((path) => getValueByPath(insightItem, path))
  );

export const useExportInsightReportToExcel = ({
  selectedSupplier,
  selectedProductGroup,
  searchQuery,
  articleId,
  environmentValuesInterface,
  climateDataA1ToA3Included,
  includedOrMissingFieldValue,
}: Props) => {
  const t = useAppTFunction();
  const toast = useToast();
  const { isOpen: isLoading, onOpen: onLoadingStart, onClose: onLoadingEnd } = useDisclosure(false);
  const { mutateAsync: loadDataForExport } = useMutateFetchSupplierItemsInsightReport();

  const exportToExcel = useCallback(async () => {
    onLoadingStart();

    try {
      const pageSizeChunk = 10000;

      const insightReportItemsDto = await fetchPaginatedDataInParallel({
        fetchFn: ({ page }) =>
          loadDataForExport({
            urlVariables: {
              mgParticipantAccountId: selectedSupplier?.supplierNumber,
              productGroup: selectedProductGroup?.groupNumber,
              text: searchQuery ?? undefined,
              articleId: articleId,
              hasFilterFields: includedOrMissingFieldValue === 'INCLUDES',
              page,
              size: pageSizeChunk,
            },
            body: {
              environmentFilters: environmentValuesInterface,
              climateFilters: climateDataA1ToA3Included ? { A1A3: true } : undefined,
            },
          }),
        maxParallellRequestsCount: 3,
      });

      const insightReportItems = insightReportItemsDto.map(SupplierItemInsight.fromDto);

      await generateXLSX({
        rows: [...rowHeaders, ...getInsightReportItemRows(insightReportItems)],
        fileName: `insightReportItems-${getISO_8601Date(new Date())}`,
      });

      toast.success(t('export.messages.fileDownloaded'));
    } catch (error: any) {
      console.error(error);
      toast.error(t('common.genericError'));
    }

    onLoadingEnd();
  }, [
    articleId,
    climateDataA1ToA3Included,
    environmentValuesInterface,
    includedOrMissingFieldValue,
    loadDataForExport,
    onLoadingEnd,
    onLoadingStart,
    searchQuery,
    selectedProductGroup?.groupNumber,
    selectedSupplier?.supplierNumber,
    t,
    toast,
  ]);

  return useMemo(
    () => ({
      isLoading,
      exportToExcel,
    }),
    [exportToExcel, isLoading]
  );
};
